// O tipo do arquivo sai dos BYTES, não do nome.
//
// Por que este módulo existe, medido em 02/09/2026 sobre as 351 recusas da
// análise no n8n (`docs/runbooks/analise-de-documentos-com-ia.md`):
//
//   formato                     casos   o que era de fato
//   sem extensão / octet-stream  105    PDF em 14 de 14 amostrados
//   HEIC                          80    o Gemini ACEITA — nada a converter
//   JFIF                          35    JPEG em 11 de 11 nos bytes
//   XML                           20    Diploma Digital do MEC
//   BMP                           19    convertível no navegador
//   ZIP · DOCX · MP4 · TIF       ~46    recusar, dizendo o que fazer
//
// O `Switch` do n8n lê a EXTENSÃO do nome do arquivo. Foi assim que ele recusou
// 80 HEIC (o padrão do iPhone) e ~20 PDFs cujo nome tinha espaço percent-encoded
// (`PDF%20CPF%20FRENTE%20`, sem extensão nenhuma no fim). O hub repetia o mesmo
// defeito em `app/src/lib/faculdade.ts`, e é por isso que este módulo é
// compartilhado: a mesma pergunta é feita no navegador (antes de subir) e na Edge
// Function (antes de chamar o modelo).
//
// ── Os detectores são EXPLICADORES, nunca portão prévio ─────────────────────
//
// `temSenha()` só roda DEPOIS de a chamada ao Gemini falhar. A medição com
// controle diz por quê: entre 120 PDFs que falharam na leitura, 16 (13,3%) têm
// `/Encrypt`; entre 120 APROVADOS, zero. O sinal é real e explica 1 em 8 — usá-lo
// como portão daria a instrução "reenvie sem senha" a ~87% de gente cujo arquivo
// estava perfeito, trocando uma mentira por outra pior.
//
// ── O que é "aceito" aqui ───────────────────────────────────────────────────
//
// Os seis mimes do bucket `secretaria` são exatamente os seis que o Gemini
// aceita (medido: HEIC real de aluno responde 200). Então "aceito" quer dizer as
// duas coisas ao mesmo tempo, e a lista não pode divergir do bucket sem alguém
// notar — o teste cobra a igualdade contra `storage.buckets`.

/** Os seis mimes do bucket `secretaria`, que são os seis que o Gemini aceita. */
export const MIMES_ACEITOS = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

export type Familia = "aceito" | "converter" | "recusado" | "desconhecido";

export type Farejado = {
  /** O que os BYTES dizem. `null` quando nenhuma assinatura casou. */
  mime: string | null;
  /** Nome curto para log e para o painel da secretaria. */
  rotulo: string;
  familia: Familia;
  /** A frase que diz ao aluno o que fazer. `null` quando o arquivo serve. */
  motivoAoAluno: string | null;
};

const u8 = (bytes: ArrayBuffer | Uint8Array): Uint8Array =>
  bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

/** Os bytes em `pos..pos+texto.length` são exatamente `texto` (ASCII)? */
function casa(b: Uint8Array, pos: number, texto: string): boolean {
  if (b.length < pos + texto.length) return false;
  for (let i = 0; i < texto.length; i++) {
    if (b[pos + i] !== texto.charCodeAt(i)) return false;
  }
  return true;
}

function casaBytes(b: Uint8Array, pos: number, esperados: number[]): boolean {
  if (b.length < pos + esperados.length) return false;
  return esperados.every((v, i) => b[pos + i] === v);
}

/**
 * A marca do PDF pode não estar no byte 0.
 *
 * A especificação manda `%PDF-` no começo, mas aceita lixo antes — e arquivos de
 * aluno passados por conversor de celular costumam ter. O leitor do Acrobat
 * procura nos primeiros 1024 bytes, e é o mesmo teto usado aqui: procurar no
 * arquivo inteiro faria um .zip contendo um PDF passar por PDF.
 */
function achaPdf(b: Uint8Array): boolean {
  const teto = Math.min(b.length, 1024);
  for (let i = 0; i <= teto - 5; i++) {
    if (casa(b, i, "%PDF-")) return true;
  }
  return false;
}

/** As marcas de `ftyp` (ISO-BMFF) que são imagem HEIF, e não vídeo. */
const MARCAS_HEIF = new Set([
  "heic", "heix", "heim", "heis", "hevc", "hevx", "hevm", "hevs",
  "mif1", "msf1", "mif2", "avif", "avis",
]);

/** As marcas de `ftyp` que são VÍDEO — recusadas, mas com nome próprio. */
const MARCAS_VIDEO = new Set([
  "isom", "iso2", "iso4", "iso5", "iso6", "mp41", "mp42", "avc1",
  "M4V ", "M4A ", "qt  ", "3gp4", "3gp5", "mmp4",
]);

/**
 * Farejar o tipo pelo conteúdo.
 *
 * Nunca estoura e nunca devolve `null` como resposta: arquivo que não casa com
 * assinatura nenhuma volta como `desconhecido`, que é uma resposta e não uma
 * ausência. É a mesma razão de `embedar()` classificar falha em vez de lançar
 * exceção — e é o que impede o chamador de tratar "não sei" como "serve".
 */
export function farejar(entrada: ArrayBuffer | Uint8Array): Farejado {
  const b = u8(entrada);

  if (b.length === 0) {
    return {
      mime: null,
      rotulo: "vazio",
      familia: "recusado",
      motivoAoAluno: "o arquivo chegou vazio (0 bytes). Reenvie o documento.",
    };
  }

  const aceito = (mime: string, rotulo: string): Farejado => ({
    mime,
    rotulo,
    familia: "aceito",
    motivoAoAluno: null,
  });

  // ── os seis que servem ────────────────────────────────────────────────────
  if (achaPdf(b)) return aceito("application/pdf", "PDF");
  if (casaBytes(b, 0, [0xff, 0xd8, 0xff])) return aceito("image/jpeg", "JPEG");
  if (casaBytes(b, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return aceito("image/png", "PNG");
  }
  if (casa(b, 0, "RIFF") && casa(b, 8, "WEBP")) return aceito("image/webp", "WebP");

  if (casa(b, 4, "ftyp")) {
    const marca = String.fromCharCode(...b.slice(8, 12));
    if (MARCAS_HEIF.has(marca)) {
      // HEIC entra direto: é o padrão do iPhone, o bucket aceita e o Gemini
      // respondeu 200 para HEIC real de aluno. Os 80 recusados na origem foram
      // recusados pela EXTENSÃO, não pelo formato.
      return aceito("image/heic", `HEIF (${marca})`);
    }
    if (MARCAS_VIDEO.has(marca)) {
      return {
        mime: "video/mp4",
        rotulo: `vídeo (${marca})`,
        familia: "recusado",
        motivoAoAluno:
          "este arquivo é um vídeo, não um documento. Envie uma foto ou um PDF do documento.",
      };
    }
    // `ftyp` de marca que não conhecemos: pode ser imagem, pode ser vídeo.
    // "desconhecido" é a resposta honesta, e quem chama decide se tenta.
    return {
      mime: null,
      rotulo: `ftyp desconhecido (${marca})`,
      familia: "desconhecido",
      motivoAoAluno: null,
    };
  }

  // ── o que dá para converter no NAVEGADOR ──────────────────────────────────
  //
  // BMP e GIF: o `canvas.toBlob` do navegador já decodifica os dois, então o
  // conserto é na tela, antes de subir — converter no servidor exigiria um
  // decodificador dentro da Edge Function para 19 casos em toda a história.
  if (casa(b, 0, "BM")) {
    return {
      mime: "image/bmp",
      rotulo: "BMP",
      familia: "converter",
      motivoAoAluno: "converta para JPEG ou PNG antes de enviar.",
    };
  }
  if (casa(b, 0, "GIF8")) {
    return {
      mime: "image/gif",
      rotulo: "GIF",
      familia: "converter",
      motivoAoAluno: "converta para JPEG ou PNG antes de enviar.",
    };
  }
  if (casaBytes(b, 0, [0x49, 0x49, 0x2a, 0x00]) || casaBytes(b, 0, [0x4d, 0x4d, 0x00, 0x2a])) {
    return {
      mime: "image/tiff",
      rotulo: "TIFF",
      familia: "converter",
      motivoAoAluno: "converta para JPEG, PNG ou PDF antes de enviar.",
    };
  }

  // ── o que não serve, com o nome certo ─────────────────────────────────────
  //
  // O DOCX é um ZIP, então os dois casam na mesma assinatura. A distinção sai do
  // conteúdo do primeiro arquivo do pacote, e ela vale a pena: "converta para
  // PDF" é a instrução certa para um .docx e errada para um .zip.
  if (casaBytes(b, 0, [0x50, 0x4b, 0x03, 0x04]) || casaBytes(b, 0, [0x50, 0x4b, 0x05, 0x06])) {
    const cabeca = new TextDecoder("latin1").decode(b.slice(0, 4096));
    const ehOffice = cabeca.includes("word/") || cabeca.includes("[Content_Types].xml");
    return ehOffice
      ? {
          mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          rotulo: "DOCX",
          familia: "recusado",
          motivoAoAluno:
            "arquivos do Word não podem ser analisados. Salve o documento como PDF e reenvie.",
        }
      : {
          mime: "application/zip",
          rotulo: "ZIP",
          familia: "recusado",
          motivoAoAluno:
            "este arquivo é um pacote compactado. Reenvie o documento em PDF, JPEG ou PNG.",
        };
  }

  // O XML aqui é o **Diploma Digital do MEC** (20 casos), que é documento de
  // verdade — só não é um formato que o modelo leia como imagem. O motivo diz
  // isso, em vez de tratá-lo como arquivo errado.
  const inicio = new TextDecoder("latin1").decode(b.slice(0, 512)).trimStart();
  if (inicio.startsWith("<?xml") || inicio.startsWith("<Diploma")) {
    return {
      mime: "application/xml",
      rotulo: "XML",
      familia: "recusado",
      motivoAoAluno:
        "o Diploma Digital vem em XML, que a análise não consegue ler. Envie o PDF de " +
        "representação visual do diploma (o que abre no visualizador do MEC).",
    };
  }

  return {
    mime: null,
    rotulo: "desconhecido",
    familia: "desconhecido",
    motivoAoAluno: null,
  };
}

/**
 * Este PDF está protegido? EXPLICADOR pós-falha — ver o cabeçalho.
 *
 * Duas decisões que vieram de medição:
 *
 * 1. **Lê o arquivo inteiro**, não a cauda. Varrer só os 8 KiB finais acha 13 dos
 *    16 e perde 18,8%, porque atualização incremental e xref stream põem o
 *    dicionário de criptografia longe do fim. O teto do bucket é 15 MiB, então o
 *    custo é conhecido.
 *
 * 2. **`latin1`, nunca `utf-8`.** O utf-8 substitui byte inválido por U+FFFD e
 *    DESLOCA tudo o que vem depois — num arquivo binário isso corrompe a busca
 *    exatamente onde ela precisa acertar. É a família da cicatriz dos bytes nulos
 *    no sentinela do conversor de BBCode: quando a pergunta é sobre bytes, a
 *    decodificação não pode inventar caractere.
 */
export function temSenha(entrada: ArrayBuffer | Uint8Array): boolean {
  const b = u8(entrada);
  if (!achaPdf(b)) return false;
  const texto = new TextDecoder("latin1").decode(b);
  // `/Encrypt` no trailer é o que marca PDF com senha (de abertura ou de
  // permissões). O espaço e a barra depois cobrem as duas formas que a
  // especificação permite (`/Encrypt 12 0 R` e `/Encrypt<<`).
  return /\/Encrypt[\s<\[\/]/.test(texto);
}

/**
 * A extensão do arquivo a partir do mime — para nomear o objeto no bucket.
 *
 * Existe para que o nome do arquivo no bucket concorde com o conteúdo dele, o
 * inverso do defeito que este módulo conserta. Quem não reconhece o mime recebe
 * `bin`: nome sem extensão é honesto, e é o farejador que responde depois.
 */
export function extensaoDoMime(mime: string | null): string {
  switch (mime) {
    case "application/pdf": return "pdf";
    case "image/jpeg": return "jpg";
    case "image/png": return "png";
    case "image/webp": return "webp";
    case "image/heic":
    case "image/heif": return "heic";
    default: return "bin";
  }
}
